import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import EmailIcon from '@mui/icons-material/Email'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { OrgAccountDetails } from '@/api/orgsAPI'
import { calculateGst } from '@/utils/billingConstants'
import { useAuth0 } from '@/utils/auth/auth0'

interface BillingInfo {
  billingLegalName: string
  billingEmail: string
  billingContactName: string
  billingContactPhone: string
  billingAddressLine1: string
  billingAddressLine2: string
  billingCity: string
  billingState: string
  billingPostalCode: string
  billingCountry: string
  taxRegistrationNumber: string
}

interface InvoicePaymentFormProps {
  tier?: number
  onSubmit: (billingInfo: BillingInfo) => Promise<void>
  isLoading?: boolean
  error?: string
  onBack?: () => void
  orgName?: string
  userEmail?: string
  userFirstName?: string
  userLastName?: string
  existingAccountDetails?: OrgAccountDetails | null
}

export function billingEmailValidationError(value: string): string | null {
  if (!value.trim()) {
    return 'Billing email is required'
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return 'Please enter a valid email address'
  }
  return null
}

export default function InvoicePaymentForm({
  tier = 4,
  onSubmit,
  isLoading = false,
  error,
  onBack,
  orgName,
  userEmail,
  userFirstName,
  userLastName,
  existingAccountDetails,
}: InvoicePaymentFormProps) {
  const theme = useTheme()
  const { getAccessTokenSilently } = useAuth0()

  const [formData, setFormData] = useState<BillingInfo>({
    billingLegalName:
      existingAccountDetails?.billing_legal_name || orgName || '',
    billingEmail: existingAccountDetails?.billing_email || userEmail || '',
    billingContactName:
      existingAccountDetails?.billing_contact_name ||
      (userFirstName && userLastName ? `${userFirstName} ${userLastName}` : ''),
    billingContactPhone: existingAccountDetails?.billing_contact_phone || '',
    billingAddressLine1: existingAccountDetails?.billing_address_line1 || '',
    billingAddressLine2: existingAccountDetails?.billing_address_line2 || '',
    billingCity: existingAccountDetails?.billing_city || '',
    billingState: existingAccountDetails?.billing_state || '',
    billingPostalCode: existingAccountDetails?.billing_postal_code || '',
    billingCountry: existingAccountDetails?.billing_country || 'New Zealand',
    taxRegistrationNumber:
      existingAccountDetails?.tax_registration_number || '',
  })

  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const [billingFrequency, setBillingFrequency] = useState<
    'monthly' | 'quarterly' | 'annual'
  >(
    (existingAccountDetails?.billing_frequency as
      | 'monthly'
      | 'quarterly'
      | 'annual') || 'annual'
  )

  // Address autocomplete state
  const [addressOptions, setAddressOptions] = useState<
    {
      a?: string
      full?: string
      address?: string
    }[]
  >([])
  const [addressLoading, setAddressLoading] = useState(false)
  const [addressSearchText, setAddressSearchText] = useState('')
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const apiUrl = import.meta.env.VITE_API_URL

  // Update form state when existing account details are loaded
  useEffect(() => {
    if (existingAccountDetails) {
      setFormData({
        billingLegalName:
          existingAccountDetails.billing_legal_name || orgName || '',
        billingEmail: existingAccountDetails.billing_email || userEmail || '',
        billingContactName:
          existingAccountDetails.billing_contact_name ||
          (userFirstName && userLastName
            ? `${userFirstName} ${userLastName}`
            : ''),
        billingContactPhone: existingAccountDetails.billing_contact_phone || '',
        billingAddressLine1: existingAccountDetails.billing_address_line1 || '',
        billingAddressLine2: existingAccountDetails.billing_address_line2 || '',
        billingCity: existingAccountDetails.billing_city || '',
        billingState: existingAccountDetails.billing_state || '',
        billingPostalCode: existingAccountDetails.billing_postal_code || '',
        billingCountry: existingAccountDetails.billing_country || 'New Zealand',
        taxRegistrationNumber:
          existingAccountDetails.tax_registration_number || '',
      })

      if (existingAccountDetails.billing_frequency) {
        setBillingFrequency(
          existingAccountDetails.billing_frequency as
            | 'monthly'
            | 'quarterly'
            | 'annual'
        )
      }
    }
  }, [existingAccountDetails, orgName, userEmail, userFirstName, userLastName])

  // Pricing based on tier (ex-GST monthly rates)
  const tierPricing = {
    0: { monthly: 20, description: 'Tier Micro' },
    1: { monthly: 125, description: 'Tier 1 - Large Organisation' },
    2: { monthly: 125, description: 'Tier 2 - Medium Organisation' },
    3: { monthly: 50, description: 'Tier 3 - Small Organisation' },
    4: { monthly: 35, description: 'Tier 4 - Mini Organisation' },
  }

  // Default to tier 4 if no tier is set or tier is invalid
  const pricing =
    tierPricing[tier as keyof typeof tierPricing] || tierPricing[4]
  const monthlyPrice = pricing.monthly

  // Calculate pricing based on frequency
  const getFrequencyMultiplier = () => {
    switch (billingFrequency) {
      case 'monthly':
        return 1
      case 'quarterly':
        return 3
      case 'annual':
        return 12
      default:
        return 12
    }
  }

  const frequencyMultiplier = getFrequencyMultiplier()
  const periodPrice = monthlyPrice * frequencyMultiplier
  const gstAmount = calculateGst(periodPrice)
  const totalPrice = periodPrice + gstAmount

  const getFrequencyLabel = () => {
    switch (billingFrequency) {
      case 'monthly':
        return 'month'
      case 'quarterly':
        return 'quarter'
      case 'annual':
        return 'year'
      default:
        return 'year'
    }
  }

  // Address autocomplete functions
  const searchAddresses = useCallback(
    async (query: string) => {
      if (!query || query.length < 3 || !apiUrl) return

      setAddressLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const response = await fetch(
          `${apiUrl}/proxy/address/search?s=${encodeURIComponent(query)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )

        if (response.ok) {
          const data = await response.json()
          setAddressOptions(data.addresses || [])
        } else {
          setAddressOptions([])
        }
      } catch (error) {
        console.error('Address search error')
        setAddressOptions([])
      } finally {
        setAddressLoading(false)
      }
    },
    [apiUrl, getAccessTokenSilently]
  )

  const debouncedAddressSearch = useCallback(
    (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        searchAddresses(query)
      }, 300)
    },
    [searchAddresses]
  )

  const handleAddressSelect = (
    selectedAddress: {
      a?: string
      full?: string
      address?: string
    } | null
  ) => {
    if (!selectedAddress) return

    // Parse the address from search results directly to avoid extra API call
    const fullAddress = selectedAddress.a || ''

    // Pattern: "Street Address, Suburb, City PostCode" -> extract components
    const addressParts = fullAddress
      .split(',')
      .map((part: string) => part.trim())

    let streetAddress = ''
    let city = ''
    let postcode = ''

    if (addressParts.length >= 1) {
      streetAddress = addressParts[0]
    }

    if (addressParts.length >= 3) {
      // Last part usually contains "City PostCode"
      const lastPart = addressParts[addressParts.length - 1]
      const postcodeMatch = lastPart.match(/\b(\d{4})\b/)

      if (postcodeMatch) {
        postcode = postcodeMatch[1]
        city = lastPart.replace(/\s*\d{4}\s*$/, '').trim()
      } else {
        city = lastPart
      }
    }

    // Update form fields immediately without API call
    setFormData((prev) => ({
      ...prev,
      billingAddressLine1: streetAddress,
      billingAddressLine2: '', // Not available in search results
      billingCity: city,
      billingState: '', // Not reliably available in search results
      billingPostalCode: postcode,
      billingCountry: 'New Zealand',
    }))

    // Clear the address search dropdown
    setAddressOptions([])

    // Clear any validation errors for address fields
    setValidationErrors((prev) => ({
      ...prev,
      billingAddressLine1: '',
      billingCity: '',
      billingPostalCode: '',
    }))
  }

  const handleInputChange = (field: keyof BillingInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.billingLegalName.trim()) {
      errors.billingLegalName = 'Legal name is required'
    }

    const billingEmailError = billingEmailValidationError(formData.billingEmail)
    if (billingEmailError) {
      errors.billingEmail = billingEmailError
    }

    if (!formData.billingContactName.trim()) {
      errors.billingContactName = 'Contact name is required'
    }

    if (!formData.billingAddressLine1.trim()) {
      errors.billingAddressLine1 = 'Address is required'
    }

    if (!formData.billingCity.trim()) {
      errors.billingCity = 'City is required'
    }

    if (!formData.billingPostalCode.trim()) {
      errors.billingPostalCode = 'Postal code is required'
    }

    if (!agreedToTerms) {
      errors.terms = 'You must agree to the terms and conditions'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Include billing frequency in the submitted data
    const submissionData = {
      ...formData,
      billingFrequency,
    }

    await onSubmit(submissionData)
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Invoice Payment Setup
      </Typography>

      {/* Billing Frequency Selector */}
      <Paper
        sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Billing Frequency
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="billing-frequency-label">
            How often would you like to be invoiced?
          </InputLabel>
          <Select
            labelId="billing-frequency-label"
            value={billingFrequency}
            label="How often would you like to be invoiced?"
            onChange={(e) =>
              setBillingFrequency(
                e.target.value as 'monthly' | 'quarterly' | 'annual'
              )
            }
          >
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly (3 months)</MenuItem>
            <MenuItem value="annual">Annual (12 months)</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Pricing Summary */}
      <Paper
        sx={{ p: 3, mb: 4, bgcolor: '#f5f9ff', border: '1px solid #e3f2fd' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Subscription Summary
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{pricing.description}</Typography>
          <Typography variant="body1">${monthlyPrice}/month</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {billingFrequency.charAt(0).toUpperCase() +
              billingFrequency.slice(1)}{' '}
            amount (ex-GST)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${periodPrice}/{getFrequencyLabel()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            GST (15%)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${gstAmount}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Total (inc. GST)
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: theme.palette.primary.main }}
          >
            ${totalPrice}/{getFrequencyLabel()}
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
          >
            <BusinessIcon color="primary" />
            Billing Information
          </Typography>

          <Grid container spacing={3}>
            {/* Organisation Details */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Legal Organisation Name"
                value={formData.billingLegalName}
                onChange={(e) =>
                  handleInputChange('billingLegalName', e.target.value)
                }
                error={!!validationErrors.billingLegalName}
                helperText={
                  validationErrors.billingLegalName ||
                  'The official registered name of your organisation'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST/Tax Registration Number"
                value={formData.taxRegistrationNumber}
                onChange={(e) =>
                  handleInputChange('taxRegistrationNumber', e.target.value)
                }
                helperText="Optional - for organisations registered for GST"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                <EmailIcon color="primary" />
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Billing Email"
                type="email"
                value={formData.billingEmail}
                onChange={(e) =>
                  handleInputChange('billingEmail', e.target.value)
                }
                error={!!validationErrors.billingEmail}
                helperText={
                  validationErrors.billingEmail ||
                  'Invoices will be sent to this email'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.billingContactName}
                onChange={(e) =>
                  handleInputChange('billingContactName', e.target.value)
                }
                error={!!validationErrors.billingContactName}
                helperText={validationErrors.billingContactName}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.billingContactPhone}
                onChange={(e) =>
                  handleInputChange('billingContactPhone', e.target.value)
                }
                helperText="Optional"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                <LocationOnIcon color="primary" />
                Billing Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={addressOptions}
                getOptionLabel={(option) =>
                  option.a || option.full || option.address || ''
                }
                loading={addressLoading}
                onInputChange={(event, newInputValue) => {
                  setAddressSearchText(newInputValue)
                  if (newInputValue && newInputValue.length >= 3) {
                    debouncedAddressSearch(newInputValue)
                  } else {
                    setAddressOptions([])
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleAddressSelect(newValue)
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for your address"
                    placeholder="Start typing your address..."
                    fullWidth
                    error={!!validationErrors.billingAddressLine1}
                    helperText={
                      validationErrors.billingAddressLine1 ||
                      'Start typing to search for your New Zealand address'
                    }
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addressLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  addressSearchText.length < 3
                    ? 'Type at least 3 characters'
                    : 'No addresses found'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.billingAddressLine1}
                onChange={(e) =>
                  handleInputChange('billingAddressLine1', e.target.value)
                }
                error={!!validationErrors.billingAddressLine1}
                helperText={
                  validationErrors.billingAddressLine1 ||
                  'Auto-filled from address search above'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.billingAddressLine2}
                onChange={(e) =>
                  handleInputChange('billingAddressLine2', e.target.value)
                }
                helperText="Optional - Suite, floor, building, etc. Auto-filled from address search"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.billingCity}
                onChange={(e) =>
                  handleInputChange('billingCity', e.target.value)
                }
                error={!!validationErrors.billingCity}
                helperText={
                  validationErrors.billingCity ||
                  'Auto-filled from address search'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State/Region"
                value={formData.billingState}
                onChange={(e) =>
                  handleInputChange('billingState', e.target.value)
                }
                helperText="Auto-filled from address search"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.billingPostalCode}
                onChange={(e) =>
                  handleInputChange('billingPostalCode', e.target.value)
                }
                error={!!validationErrors.billingPostalCode}
                helperText={
                  validationErrors.billingPostalCode ||
                  'Auto-filled from address search'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.billingCountry}
                onChange={(e) =>
                  handleInputChange('billingCountry', e.target.value)
                }
                helperText="Auto-filled from address search"
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            {/* Terms Agreement */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <a
                      href="https://www.fundsorter.com/termsofservice"
                      target="_blank"
                      style={{ color: theme.palette.primary.main }}
                    >
                      Terms and Conditions
                    </a>{' '}
                    and authorise invoicing for this subscription
                  </Typography>
                }
              />
              {validationErrors.terms && (
                <Typography
                  variant="caption"
                  color="error"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  {validationErrors.terms}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Form Actions */}
          <Box
            sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}
          >
            {onBack && (
              <Button
                variant="outlined"
                disableRipple
                onClick={onBack}
                disabled={isLoading}
                size="large"
                sx={{
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Back
              </Button>
            )}
            <Button
              disableRipple
              type="submit"
              variant="contained"
              disabled={isLoading}
              size="large"
              sx={{ minWidth: 200 }}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
            >
              {isLoading ? 'Setting Up...' : 'Set Up Invoice Billing'}
            </Button>
          </Box>
        </Paper>
      </form>
    </Box>
  )
}
